import React, { useState } from "react";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CircleIcon from "@mui/icons-material/Circle";

import SimplePaginationControl from "common/Pagination/SimplePaginationControl";
import { CardLevelOne } from "common/DS/ElevatedCard";
import List from "common/DS/List";
import { TripleListItem } from "common/DS/ListItem";
import { H6, InlineBody2, InlineCaption } from "common/DS/Text";
import moment from "moment-timezone";
import { commentMomentLocale } from "lib/date";
import COLOR from "lib/colors";
import type { HubComponentQuery$data } from "__generated__/HubComponentQuery.graphql";

const PAGE_SIZE = 10;

type Props = {
    messages: HubComponentQuery$data["hubMessages"];
};

const HubMessagesList = ({ messages }: Props) => {
    const [page, setPage] = useState(1);

    if (messages.length === 0) return null;
    const numPages = Math.ceil(messages.length / PAGE_SIZE);
    const pageStart = (page - 1) * PAGE_SIZE;
    const pageMessages = messages.slice(pageStart, pageStart + PAGE_SIZE);

    return (
        <>
            <CardLevelOne className="w100">
                <H6 className="hub__messages-header color-navy-high">Messages</H6>
                <List disablePadding>
                    {pageMessages.map((message, index) => {
                        const isConversation = message.type === "CONVERSATION";
                        const Icon: React.ElementType = isConversation
                            ? AssignmentOutlinedIcon
                            : CampaignOutlinedIcon;
                        const timeAgo = commentMomentLocale(
                            moment.utc(message.timeCreated),
                        ).fromNow();

                        return (
                            <TripleListItem
                                key={pageStart + index}
                                variant={index < pageMessages.length - 1 ? "divided" : undefined}
                                hover
                                className="hub__messages-list-item"
                                component="a"
                                href={message.url ?? undefined}
                                content={
                                    <span className={!message.isRead ? "fw-bold" : ""}>
                                        {message.title}
                                    </span>
                                }
                                extraIcons={[
                                    !message.isRead ? (
                                        <CircleIcon key="u" className="hub__messages-unread-dot" />
                                    ) : null,
                                ].filter(Boolean)}
                                secondary={
                                    <InlineBody2 className="color-navy-medium">
                                        {message.description}
                                    </InlineBody2>
                                }
                                meta={
                                    <InlineCaption className="color-navy-medium">
                                        {timeAgo}
                                    </InlineCaption>
                                }
                            >
                                <Icon style={{ color: COLOR["navy-medium"], fontSize: 18 }} />
                            </TripleListItem>
                        );
                    })}
                </List>
            </CardLevelOne>
            {numPages > 1 && (
                <div className="hub__messages-pagination">
                    <SimplePaginationControl
                        numPages={numPages}
                        currentPage={page}
                        onPageSelect={setPage}
                    />
                </div>
            )}
        </>
    );
};

export default HubMessagesList;
